import { NotifyHelper } from 'classes/helpers/notify.helper';
import { PitchDesignHelper } from 'classes/helpers/pitch-design.helper';
import lightFormat from 'date-fns/lightFormat';
import { CSVFormat, QualityError } from 'enums/csv';
import { t } from 'i18next';
import { CSV_FORMAT_DEFS } from 'interfaces/i-csv';
import { AimingHelper } from 'lib_ts/classes/aiming.helper';
import { ArrayHelper } from 'lib_ts/classes/array.helper';
import { BallHelper } from 'lib_ts/classes/ball.helper';
import { CSVHelper } from 'lib_ts/classes/csv.helper';
import { MPH_TO_FTPS, meanObject } from 'lib_ts/classes/math.utilities';
import { TrajHelper } from 'lib_ts/classes/trajectory.helper';
import { LOCAL_DATE_FORMAT } from 'lib_ts/enums/datetime.enums';
import { MlbSportId } from 'lib_ts/enums/mlb-stats-api/base.enum';
import {
  BREAKING_PITCHES,
  BuildPriority,
  lookupPitchType,
} from 'lib_ts/enums/pitches.enums';
import { DictOf } from 'lib_ts/interfaces/common/i-dictionary';
import {
  ICSVRow,
  ICustomBreaks,
  ICustomSpins,
  IHawkeye,
  IStatCast,
} from 'lib_ts/interfaces/csv';
import { DUMMY_SEASON } from 'lib_ts/interfaces/mlb-stats-api/i-player';
import {
  DEFAULT_PLATE,
  IBallDetails,
  IBallState,
  IPitch,
  IPlateLoc,
  ITrajectory,
  ITrajektRefBreak,
} from 'lib_ts/interfaces/pitches';
import {
  DEFAULT_SEAMS,
  IPosition,
  ISeamOrientation,
  ISpin,
} from 'lib_ts/interfaces/pitches/i-base';
import { clone, compact, isNil } from 'lodash';
import { v4 } from 'uuid';

const MAX_SEAMS_DELTA_DEG = 5;

// needed to group pitches by pitcher when averaging if not provided via csv
const DEFAULT_PITCHER_NAME = 'Custom';

// used to ignore placeholder name provided by legacy csv exports
const PLACEHOLDER_PITCHER_NAME = '--';

type IParsedData = ICustomSpins | ICustomBreaks | IHawkeye | IStatCast;

/** for use in assembling average value */
interface IReleaseValue {
  bs: IPosition | undefined;
  traj: IPosition | undefined;
}

export class CSVBall {
  // MARK: detectFormat
  static detectFormat(firstRow: any) {
    const keys = Object.keys(firstRow);

    const found = CSV_FORMAT_DEFS.find((def) => {
      const missingKeys = def.requiredHeaders.filter((h) => !keys.includes(h));
      return missingKeys.length === 0;
    });

    if (found) {
      return found.format;
    }

    return CSVFormat.Unknown;
  }

  // MARK: getAverageRelease
  static getAverageRelease(balls: CSVBall[]): IReleaseValue {
    const avgBsRelease = meanObject(
      compact(
        balls.map((m) => {
          if (!m.bs) {
            return undefined;
          }

          const output: IPosition = {
            px: m.bs.px,
            py: m.bs.py,
            pz: m.bs.pz,
          };

          return output;
        })
      )
    );

    const avgTrajRelease = meanObject(
      compact(
        balls.map((m) => {
          if (!m.bs) {
            return undefined;
          }

          const output: IPosition = {
            px: m.bs.px,
            py: m.bs.py,
            pz: m.bs.pz,
          };

          return output;
        })
      )
    );

    return {
      bs: avgBsRelease,
      traj: avgTrajRelease,
    };
  }

  // MARK: setAverageRelease
  private setAverageRelease(position: IReleaseValue): void {
    if (!this.bs) {
      return;
    }

    if (!position.bs) {
      return;
    }

    if (!this.traj) {
      return;
    }

    if (!position.traj) {
      return;
    }

    const tempBs = clone(this.bs);
    tempBs.px = position.bs.px;
    tempBs.py = position.bs.py;
    tempBs.pz = position.bs.pz;

    const tempTraj = clone(this.traj);
    tempTraj.px = position.traj.px;
    tempTraj.py = position.traj.py;
    tempTraj.pz = position.traj.pz;

    // update with newly aimed chars
    const aimed = AimingHelper.aimBsTraj({
      releasePos: {
        px: tempTraj.px,
        py: tempTraj.py,
        pz: tempTraj.pz,
      },
      bs: tempBs,
      traj: tempTraj,
      plate: this.plate,
    });

    this.bs = aimed.bs;
    this.traj = aimed.traj;
    this.plate = aimed.plate;
  }

  /** groups balls by pitcher (only)
   * updates each ball's release position (on details > pitchChars) with the average values for each pitcher
   * only works on balls where ms is defined
   */
  // MARK: updateAverageReleasesByPitcher
  static updateAverageReleasesByPitcher(balls: CSVBall[]): void {
    try {
      const byPitchers = ArrayHelper.groupByFn(balls, (m) => m.details.pitcher);

      for (const pitcher of Object.keys(byPitchers)) {
        const pBalls = byPitchers[pitcher];

        if (!pBalls || pBalls.length === 0) {
          continue;
        }

        /** update balls' pitch chars with avg value */
        const avgRelease = CSVBall.getAverageRelease(pBalls);
        pBalls.forEach((m) => m.setAverageRelease(avgRelease));
      }
    } catch (e) {
      NotifyHelper.error({
        message_md: t('common.request-failed-msg'),
      });
      console.error(e);
    }
  }

  /** groups balls by pitcher, computes the average release for each pitcher
   *  groups balls by pitcher + type, computes an average bs for each group
   *  averages the bs, traj, and breaks of pitches in groups
   *  returns a list of CSV balls corresponding to the averages
   */
  // MARK: getAveragesByPitcherAndType
  static async getAveragesByPitcherAndType(
    balls: CSVBall[]
  ): Promise<CSVBall[]> {
    try {
      const byPitchers = ArrayHelper.groupByFn(balls, (m) => m.details.pitcher);

      const output: CSVBall[] = [];

      for (const pitcher of Object.keys(byPitchers)) {
        const pBalls = byPitchers[pitcher];

        if (!pBalls || pBalls.length === 0) {
          continue;
        }

        /** average the releases for this pitcher and store it in the dictionary */
        const pRelease = CSVBall.getAverageRelease(pBalls);

        const byTypes = ArrayHelper.groupByFn(pBalls, (m) => m.details.type);

        for (const type of Object.keys(byTypes)) {
          /** all balls for this pitcher + pitch type combination */
          const ptBalls = byTypes[type];

          if (!ptBalls || ptBalls.length === 0) {
            continue;
          }

          /** base output entry loosely on the first ball of this combination */
          const wholeBall = ptBalls.find((ball) => ball.bs && ball.traj);

          if (!wholeBall) {
            console.error(
              `Could not find a ball with bs, ms, and traj for ${pitcher} + ${type}`
            );
            continue;
          }

          /** compute average and update each ball's attributes */
          const bss = compact(ptBalls.map((m) => m.bs));
          const trajs = compact(ptBalls.map((m) => m.traj));
          const breaks = compact(ptBalls.map((m) => m.breaks));

          const avgBall = wholeBall.clone();

          /** additional mappings for avg values not in bs need to go here */
          avgBall.details.name = `${pitcher} ${type} (Average)`;

          avgBall.bs = meanObject(bss);
          avgBall.traj = meanObject(trajs);
          avgBall.breaks = meanObject(breaks);

          /** replace the release position with average */
          avgBall.setAverageRelease(pRelease);

          output.push(avgBall);
        }
      }

      return output;
    } catch (e) {
      NotifyHelper.error({
        message_md: t('common.request-failed-msg'),
      });
      console.error(e);
      return [];
    }
  }

  readonly _id: string;

  readonly releaseFt: number;
  readonly format: CSVFormat;
  readonly priority: BuildPriority;

  /** CSV record that informed this instance, track this for reporting errors with data */
  readonly rawData: ICSVRow;

  /** numeric columns need to be parsed into floats from the strings */
  readonly parsedData?: IParsedData;

  /** track the desired plate location for the pitch (if provided), defaults to DEFAULT_PLATE */
  plate: IPlateLoc = DEFAULT_PLATE;

  bs: IBallState | undefined;
  traj: ITrajectory | undefined;
  breaks: ITrajektRefBreak | undefined;
  seams: ISeamOrientation | undefined;

  // misc metadata stuff
  details: IBallDetails;

  fatalError: string | undefined;

  zeroBreakingSeams: boolean;

  // MARK: constructor
  constructor(config: {
    // for extrapolation convenience
    releaseFt: number;
    format: CSVFormat;
    data: ICSVRow;
    index: number;
    priority: BuildPriority;
    zeroBreakingSeams: boolean;
    videosDict?: DictOf<string>;
  }) {
    this._id = v4();

    this.releaseFt = config.releaseFt;
    this.format = config.format;
    this.rawData = config.data;
    this.zeroBreakingSeams = config.zeroBreakingSeams;

    this.priority = config.priority;

    this.details = CSVBall.getDetails(
      config.index,
      this.rawData,
      config.videosDict
    );

    const parsedData = CSVBall.getParsedData(this.format, this.rawData);

    if (!parsedData) {
      this.fatalError = 'Failed to parse data';
      return;
    }

    this.parsedData = parsedData;

    this.prepareData(parsedData);
  }

  // MARK: rebuild
  rebuild() {
    try {
      if (!this.bs) {
        throw new Error(`Cannot rebuild ${this._id} w/o bs`);
      }

      if (!this.traj) {
        throw new Error(`Cannot rebuild ${this._id} w/o traj`);
      }

      const results = PitchDesignHelper.deriveBsAndTraj(
        {
          speedMph: BallHelper.getSpeed(this.bs),
          spins: this.bs,
          spinExt: BallHelper.convertSpinToSpinExt(this.bs),
          breaks: TrajHelper.getBreaks(this.traj),
          seams: this.seams ?? BallHelper.getSeamsFromBS(this.bs),
          plate: this.plate,
          releasePos: {
            // * shouldn't matter, but uses bs (instead of traj) position for release position
            px: this.bs.px,
            py: this.bs.py,
            pz: this.bs.pz,
          },
          videoIDs: {
            windupVideoID: null,
            stretchVideoID: null,
          },
        },
        this.priority
      );

      this.bs = results.bs;
      this.traj = results.traj;

      this.breaks = TrajHelper.getBreaks(results.traj);
      this.plate = TrajHelper.getPlateLoc(results.traj);
    } catch (e) {
      console.error(this.details.name, e);
    }
  }

  // MARK: getDetails
  private static getDetails(
    index: number,
    data: ICSVRow,
    videosDict: DictOf<string> | undefined
  ) {
    const output: IBallDetails = {
      name:
        data.PitchTitle ??
        `${lightFormat(new Date(), LOCAL_DATE_FORMAT)} Custom Pitch ${(
          index + 1
        )
          .toFixed(0)
          .padStart(4, '0')}`,
      type: lookupPitchType(data.PitchType),
      year: data.PitchYear,
      pitcher: data.PitcherFullName?.trim() ?? DEFAULT_PITCHER_NAME,
      team: data.PitcherTeam?.trim(),
      windupVideoID: null,
      stretchVideoID: null,
    };

    /** try to auto-select video based on first match by VideoTitle, then by VideoID */
    if (videosDict) {
      output.windupVideoID =
        videosDict[`${data.WindupVideoID}`.trim()] ??
        videosDict[`${data.VideoTitleWindup}`.trim()] ??
        // handling for legacy VideoID and VideoTitle csv fields
        videosDict[`${data.VideoID}`.trim()] ??
        videosDict[`${data.VideoTitle}`.trim()] ??
        '';

      output.stretchVideoID =
        videosDict[`${data.StretchVideoID}`.trim()] ??
        videosDict[`${data.VideoTitleStretch}`.trim()] ??
        '';
    }

    return output;
  }

  // MARK: clone
  /** returns an instance that's reconstructed from the starting conditions of the original */
  clone(): CSVBall {
    const output = new CSVBall({
      releaseFt: this.releaseFt,
      format: this.format,
      data: this.rawData,
      index: -1,
      priority: this.priority ?? BuildPriority.Spins,
      zeroBreakingSeams: this.zeroBreakingSeams,
    });

    // b/c these may have changed since the original parsed result from the rawData
    output.bs = clone(this.bs);
    output.traj = clone(this.traj);
    output.breaks = clone(this.breaks);
    output.plate = clone(this.plate);

    return output;
  }

  // MARK: getParsedData
  private static getParsedData(format: CSVFormat, data: ICSVRow) {
    try {
      switch (format) {
        case CSVFormat.CustomBreaks: {
          return CSVHelper.createCustomBreaks(data);
        }

        case CSVFormat.CustomSpins: {
          return CSVHelper.createCustomSpins(data);
        }

        case CSVFormat.Hawkeye: {
          return CSVHelper.createHawkeye(data);
        }

        case CSVFormat.Statcast: {
          return CSVHelper.createStatCast(data);
        }

        default: {
          throw Error(`Invalid format detected: ${format}`);
        }
      }
    } catch (e) {
      console.error(e);
      return undefined;
    }
  }

  // MARK: getWarnings
  getWarnings() {
    try {
      if (!this.parsedData) {
        throw Error('getWarnings cannot proceed without parsedData');
      }

      const validationWarnings: string[] = [];

      // validate spin/break values
      if (this.bs && this.priority) {
        const pdErrors = PitchDesignHelper.getBulkPitchUploadErrors(
          {
            bs: this.bs,
            breaks: this.breaks,
          },
          this.priority
        );

        validationWarnings.push(...pdErrors);
      }

      const qualityWarnings: QualityError[] = [];

      if (this.format === CSVFormat.Statcast) {
        qualityWarnings.push(QualityError.Statcast);
      }

      if (!CSVHelper.hasSeamDetails(this.parsedData)) {
        qualityWarnings.push(QualityError.SeamDetails);
      }

      if (
        this.priority === BuildPriority.Breaks &&
        CSVHelper.hasBreakColumns(this.parsedData) &&
        !CSVHelper.hasBreakDetails(this.parsedData)
      ) {
        // break columns exist but valid breaks data was not found
        qualityWarnings.push(QualityError.BreakDetails);
      }

      return {
        quality: ArrayHelper.unique(qualityWarnings),
        validation: ArrayHelper.unique(validationWarnings),
      };
    } catch (e) {
      console.error(e);
      return undefined;
    }
  }

  // MARK: prepareData
  private prepareData(parsedData: IParsedData) {
    try {
      // as per the file, should match results of preparing when using breaks priority
      const parsedBreaks: ITrajektRefBreak | undefined =
        !isNil(parsedData.HorizontalBreakIn) &&
        !isNil(parsedData.VerticalBreakIn)
          ? {
              // CSV input data is in Hawkeye reference, flip it to get trajekt frame of ref
              xInches: -(parsedData.HorizontalBreakIn ?? 0),
              zInches: parsedData.VerticalBreakIn ?? 0,
            }
          : undefined;

      const isBreaking =
        BREAKING_PITCHES.findIndex((group) =>
          group.aliases?.includes(this.rawData.PitchType.toLowerCase())
        ) !== -1;

      const parsedSeams =
        isBreaking && this.zeroBreakingSeams
          ? DEFAULT_SEAMS // only default to 0,0 for breaking pitches
          : (CSVHelper.getSeams(parsedData) ?? DEFAULT_SEAMS); // otherwise try to parse from the data

      console.debug(`${this.details.name}: parsed seams`, parsedSeams);

      // as per the file, should match results of preparing
      const parsedPlate = CSVHelper.getPlateLoc(parsedData);

      /** set chars */
      switch (this.format) {
        case CSVFormat.CustomSpins: {
          this.prepareCustomSpins(
            parsedData as ICustomSpins,
            parsedPlate,
            parsedSeams,
            parsedBreaks
          );
          break;
        }

        case CSVFormat.CustomBreaks: {
          this.prepareCustomBreaks(
            parsedData as ICustomBreaks,
            parsedPlate,
            parsedSeams,
            parsedBreaks
          );
          break;
        }

        case CSVFormat.Hawkeye: {
          this.prepareHawkeye(
            parsedData as IHawkeye,
            parsedPlate,
            parsedSeams,
            parsedBreaks
          );
          break;
        }

        case CSVFormat.Statcast: {
          this.prepareStatcast(
            parsedData as IStatCast,
            parsedPlate,
            parsedSeams,
            parsedBreaks
          );
          break;
        }

        default: {
          throw Error(
            `prepareData encountered unexpected format: ${this.format}`
          );
        }
      }

      this.checkSeamsDeltas();
    } catch (e) {
      console.error(this.details.name, e);

      this.fatalError =
        'Failed to prepare ball data, see console for more details';
    }
  }

  // MARK: prepareHawkeye
  /** StateTransformService > buildPitches should be called after this */
  private prepareHawkeye(
    data: IHawkeye,
    plate: IPlateLoc,
    seams: ISeamOrientation,
    breaks: ITrajektRefBreak | undefined
  ) {
    try {
      const traj: ITrajectory = {
        px: data.x0,
        py: data.y0,
        pz: data.z0,
        vx: data.vx0,
        vy: data.vy0,
        vz: data.vz0,
        ax: data.ax0,
        ay: data.ay0,
        az: data.az0,
        wnet: BallHelper.getNetSpin({
          wx: data.spinX,
          wy: data.spinY,
          wz: data.spinZ,
        }),
      };

      // use releaseY only if provided, otherwise use the releaseFt
      const safeTraj = TrajHelper.extrapolateTrajectory(
        traj,
        data.releaseY ?? this.releaseFt
      );

      const safeSpin = CSVHelper.getHawkeyeSpin(data, safeTraj);

      const result = PitchDesignHelper.deriveBsAndTraj(
        {
          speedMph: TrajHelper.getSpeedFromTraj(safeTraj),
          spins: safeSpin,
          spinExt: BallHelper.convertSpinToSpinExt(safeSpin),
          breaks: breaks ?? TrajHelper.getBreaks(safeTraj),
          seams: seams,
          plate: plate,
          releasePos: {
            px: safeTraj.px,
            py: safeTraj.py,
            pz: safeTraj.pz,
          },
          // not actually used for fn
          videoIDs: {
            windupVideoID: null,
            stretchVideoID: null,
          },
        },
        this.priority ?? BuildPriority.Breaks // coalesce should not actually trigger
      );

      this.seams = seams;

      this.bs = result.bs;
      this.traj = result.traj;

      this.breaks = TrajHelper.getBreaks(result.traj); // sanity check, make sure they align w/ traj
      this.plate = TrajHelper.getPlateLoc(result.traj); // sanity check
    } catch (e) {
      console.error(this.details.name, e);

      this.fatalError =
        'Failed to prepare Hawkeye data, see console for more details';
    }
  }

  // MARK: prepareCustomSpins
  /** StateTransformService > buildPitches should be called after this */
  private prepareCustomSpins(
    data: ICustomSpins,
    plate: IPlateLoc,
    seams: ISeamOrientation,
    breaks: ITrajektRefBreak | undefined
  ) {
    try {
      if (this.priority === BuildPriority.Breaks && !breaks) {
        this.fatalError = 'Cannot proceed without breaks info';
        return;
      }

      const result = PitchDesignHelper.deriveBsAndTraj(
        {
          speedMph: data.ReleaseV,
          spins: {
            wx: data.SpinX,
            wy: data.SpinY,
            wz: data.SpinZ,
          },
          spinExt: BallHelper.convertSpinToSpinExt({
            wx: data.SpinX,
            wy: data.SpinY,
            wz: data.SpinZ,
          }),
          // ? doesn't matter too much (while priority is not breaks)
          breaks: breaks ?? {
            // only used when priority is not breaks
            xInches: 0,
            zInches: 0,
          },
          seams: seams,
          plate: plate,
          releasePos: {
            px: data.ReleaseX,
            py: data.ReleaseY,
            pz: data.ReleaseZ,
          },
          // not actually used for fn
          videoIDs: {
            windupVideoID: null,
            stretchVideoID: null,
          },
        },
        this.priority ?? BuildPriority.Spins // coalesce should not actually trigger
      );

      this.seams = seams;

      this.bs = result.bs;
      this.traj = result.traj;

      this.breaks = TrajHelper.getBreaks(result.traj); // sanity check
      this.plate = TrajHelper.getPlateLoc(result.traj); // sanity check
    } catch (e) {
      console.error(this.details.name, e);

      this.fatalError =
        'Failed to prepare custom spins data, see console for more details';
    }
  }

  // MARK: prepareCustomBreaks
  /** StateTransformService > buildPitches should be called after this */
  private prepareCustomBreaks(
    data: ICustomBreaks,
    plate: IPlateLoc,
    seams: ISeamOrientation,
    breaks: ITrajektRefBreak | undefined
  ) {
    try {
      // this wuold only proceed if you happen to be on breaks
      if (this.priority !== BuildPriority.Breaks) {
        this.fatalError = 'Mismatched priority for custom breaks file';
        return;
      }

      if (!breaks) {
        this.fatalError = 'Explicit breaks are required';
        return;
      }

      const fallbackWy =
        data.ReleaseX > 0
          ? 1_000 // LHP fallback
          : -1_000; // RHP fallback

      const safeSpin: ISpin = {
        wx: 0,
        wy: data.SpinY ?? fallbackWy,
        wz: 0,
      };

      const result = PitchDesignHelper.deriveBsAndTraj(
        {
          speedMph: data.ReleaseV,
          spins: safeSpin,
          spinExt: BallHelper.convertSpinToSpinExt(safeSpin),
          breaks: breaks,
          seams: seams,
          plate: plate,
          releasePos: {
            px: data.ReleaseX,
            py: data.ReleaseY,
            pz: data.ReleaseZ,
          },
          // not actually used for fn
          videoIDs: {
            windupVideoID: null,
            stretchVideoID: null,
          },
        },
        this.priority
      );

      this.seams = seams;

      this.bs = result.bs;
      this.traj = result.traj;

      this.breaks = TrajHelper.getBreaks(result.traj);
      this.plate = TrajHelper.getPlateLoc(result.traj);
    } catch (e) {
      console.error(this.details.name, e);

      this.fatalError =
        'Failed to prepare custom breaks data, see console for more details';
    }
  }

  // MARK: prepareStatcast
  /** StateTransformService > buildPitches should be called after this */
  private prepareStatcast(
    data: IStatCast,
    plate: IPlateLoc,
    seams: ISeamOrientation,
    breaks: ITrajektRefBreak | undefined
  ) {
    try {
      // ? extrapolate from 50' to our release position
      const vx50 = data.vx0; // In FTPS
      const vy50 = data.vy0; // In FTPS
      const vz50 = data.vz0; // In FTPS
      const tr = TrajHelper.calc_tr({
        vy: vy50,
        ay: data.ay,
        py: 50,
        new_py: data.release_pos_y,
      });
      const vxRel = vx50 + data.ax * tr;
      const vyRel = vy50 + data.ay * tr;
      const vzRel = vz50 + data.az * tr;

      const speedFTPS = BallHelper.getSpeed({
        vx: vxRel,
        vy: vyRel,
        vz: vzRel,
      });

      const traj: ITrajectory = {
        px: data.release_pos_x,
        py: data.release_pos_y,
        pz: data.release_pos_z,
        vx: vxRel,
        vy: vyRel,
        vz: vzRel,
        ax: data.ax,
        ay: data.ay,
        az: data.az,
        wnet: data.release_spin_rate,
      };

      const getSafeSpin = () => {
        const naive = CSVHelper.getSpin(data);
        if (naive) {
          return naive;
        }

        const fallbackWy =
          data.release_pos_x > 0
            ? 1_000 // LHP fallback
            : -1_000; // RHP fallback

        const exBs = BallHelper.getBsFromTraj(traj, seams, fallbackWy);

        const exSpin: ISpin = {
          wx: exBs.wx,
          wy: exBs.wy,
          wz: exBs.wz,
        };

        return exSpin;
      };

      const safeSpin = getSafeSpin();

      const result = PitchDesignHelper.deriveBsAndTraj(
        {
          speedMph: speedFTPS / MPH_TO_FTPS,
          spins: safeSpin,
          spinExt: BallHelper.convertSpinToSpinExt(safeSpin),
          breaks: breaks ?? TrajHelper.getBreaks(traj),
          seams: seams,
          plate: plate,
          releasePos: {
            px: data.release_pos_x,
            py: data.release_pos_y,
            pz: data.release_pos_z,
          },
          // not actually used for fn
          videoIDs: {
            windupVideoID: null,
            stretchVideoID: null,
          },
        },
        this.priority ?? BuildPriority.Breaks // coalesce should not actually trigger
      );

      this.seams = seams;

      this.bs = result.bs;
      this.traj = result.traj;

      this.breaks = TrajHelper.getBreaks(result.traj); // sanity check
      this.plate = TrajHelper.getPlateLoc(result.traj); // sanity check
    } catch (e) {
      console.error(this.details.name, e);

      this.fatalError =
        'Failed to prepare Statcast data, see console for more details';
    }
  }

  // MARK: prepForSave
  /** add extra attributes, e.g. before saving */
  prepForSave(): Partial<IPitch> {
    // don't save '--' or 'Custom' names to database
    const pitcher = [DEFAULT_PITCHER_NAME, PLACEHOLDER_PITCHER_NAME].includes(
      this.details.pitcher
    )
      ? undefined
      : this.details.pitcher;

    const output: Partial<IPitch> = {
      _id: this._id,
      name: this.details.name,
      year: this.details.year,

      windupVideoID: this.details.windupVideoID,
      stretchVideoID: this.details.stretchVideoID,

      /** server will handle converting this via reverse lookup for a formal pitch type, defaulting to FF */
      type: this.details.type,

      pitcher: pitcher,
      team: this.details.team,

      mlb_repertoire: this.rawData.MlbPlayerPk
        ? {
            season: DUMMY_SEASON, // will be overridden by server
            sportId: MlbSportId.MLB, // will be overridden by server
            playerPk: this.rawData.MlbPlayerPk,
          }
        : undefined,

      bs: this.bs,
      traj: this.traj,

      priority: this.priority,
      breaks: this.breaks,
      seams: this.seams,
    };

    return output;
  }

  // MARK: checkSeamsDeltas
  // should only be called after building the pitch
  private checkSeamsDeltas() {
    const { seams, bs } = this;

    if (!seams) {
      console.warn({
        event: `CSVBall: seams should not be empty`,
        name: this.details.name,
        bs: bs,
        seams: seams,
      });
      this.fatalError = '';
      return;
    }

    if (!bs) {
      console.warn({
        event: `CSVBall: bs should not be empty`,
        name: this.details.name,
        bs: bs,
        seams: seams,
      });
      return;
    }

    const bsSeams = BallHelper.getSeamsFromBS(bs);

    const delta_lat_deg = seams.latitude_deg - bsSeams.latitude_deg;
    const delta_lon_deg = seams.longitude_deg - bsSeams.longitude_deg;

    if (
      Math.abs(delta_lat_deg) > MAX_SEAMS_DELTA_DEG ||
      Math.abs(delta_lon_deg) > MAX_SEAMS_DELTA_DEG
    ) {
      console.warn({
        event: `CSVBall: large delta between uploaded and constructed seams`,
        name: this.details.name,
        delta_lat_deg,
        delta_lon_deg,
        bs: bs,
        seams: seams,
        bsSeams,
      });
    }
  }
}
